'use client';

import { Divider, Typography, Stack } from '@mui/material';
import { useState, useEffect } from 'react';
import { ContainedButton } from '../contained-button';
import { MobileSwipeableDrawer } from '../mobile-swipeable-drawer';
import { useIsMobile } from '../../hooks';
import { memoIcon, phoneIcon } from '../../config/icons';
import { WhatsappButton } from './whatsapp-button';
import { ConsentCheckbox } from './consent-checkbox';
import { TProject } from '../../types/config';

const ButtonsWrapper = ({
  buttonsWithStack,
  children,
}: {
  buttonsWithStack?: boolean;
  children: React.ReactNode;
}) => {
  return buttonsWithStack ? (
    <Stack direction="row" justifyContent="space-between" spacing={1} width="100%">
      {children}
    </Stack>
  ) : (
    <>{children}</>
  );
};

export type PhoneButtonProps = {
  project: TProject;
  showLineAnimation?: boolean;
  withDivider?: boolean;
  onShowPhoneNumber?: () => void;
  onCallSeller?: () => void;
  handleWhatsappClick?: () => void;
  t: (key: string, options?: any) => string;
  consentCheckbox?: boolean;
  useExternalConsent?: boolean;
  externalConsentChecked?: boolean;
  onExternalConsentValidationFailed?: () => void;
  buttonsWithStack?: boolean;
};

export function PhoneButton({
  project,
  showLineAnimation,
  withDivider,
  onShowPhoneNumber,
  onCallSeller,
  handleWhatsappClick,
  t,
  consentCheckbox = false,
  useExternalConsent = false,
  externalConsentChecked = false,
  onExternalConsentValidationFailed,
  buttonsWithStack = true,
}: PhoneButtonProps) {
  // Always call all hooks first - no early returns before hooks
  const [showNumber, setShowNumber] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentError, setConsentError] = useState('');
  const isMobile = useIsMobile();

  // Clear consent error when consent is checked (for independent PhoneButton usage)
  useEffect(() => {
    if (!useExternalConsent && consentChecked && consentError) {
      setConsentError('');
    }
  }, [consentChecked, consentError, useExternalConsent]);

  // Clear any existing consent error when external consent becomes checked
  useEffect(() => {
    if (useExternalConsent && externalConsentChecked) {
      setConsentError('');
    }
  }, [useExternalConsent, externalConsentChecked]);

  // Now we can safely check for phone availability after all hooks
  const phoneMobile = project?.phoneMobile;
  const phoneDesktop = project?.phoneDesktop;
  const phoneCurrent = isMobile ? phoneMobile || phoneDesktop : phoneDesktop || phoneMobile;

  if (!phoneCurrent) return null;

  const countryCode = phoneCurrent.replace('+', '').slice(0, 3);
  const phone = phoneCurrent.replace(countryCode, '');
  const showPlus = countryCode === '972';
  const phoneNumber = `${showPlus ? '+' : ''}${countryCode}-${phone}`;

  const handlePhoneNumber = () => {
    // Clear any existing consent error
    setConsentError('');

    // Determine which consent state to use
    const shouldCheckConsent = consentCheckbox; // Always check if consentCheckbox is enabled
    const isConsentChecked = useExternalConsent ? externalConsentChecked : consentChecked;

    // Check consent validation if consent checkbox is enabled
    if (shouldCheckConsent && !isConsentChecked) {
      if (useExternalConsent && onExternalConsentValidationFailed) {
        // Trigger external validation (LeadForm will handle the error display)
        onExternalConsentValidationFailed();
      } else if (!useExternalConsent) {
        setConsentError(t('info.consentRequired'));
      }
      return;
    }

    if (!showNumber) {
      if (isMobile) {
        setIsDrawerOpen(true);
      } else {
        setShowNumber(true);
      }

      if (onShowPhoneNumber) {
        onShowPhoneNumber();
      }
    } else {
      if (onCallSeller) {
        onCallSeller();
      }
      handleCall();
    }
  };

  const handleCall = () => {
    window.location.href = `tel:${phoneCurrent}`;
  };

  // the phone number should always be LTR
  return (
    <>
      {withDivider && !isMobile && (
        <Divider>
          <Typography color="grey.400" fontWeight={600}>
            {t('labels.or')}
          </Typography>
        </Divider>
      )}

      {consentCheckbox && !useExternalConsent && (
        <ConsentCheckbox
          checked={consentChecked}
          onChange={setConsentChecked}
          error={consentError}
          t={t}
        />
      )}

      <ButtonsWrapper buttonsWithStack={buttonsWithStack}>
        <ContainedButton
          color="success"
          label={!showNumber ? t('labels.showPhoneNumber') : phoneNumber}
          onClick={handlePhoneNumber}
          icon={!isMobile ? phoneIcon : undefined}
          showLineAnimation={showLineAnimation}
          labelDirection="ltr"
          data-treedistestid="button-phone-contact"
          buttonSx={{ flex: 1 }}
        />
        {!!project?.whatsappNumber && (
          <WhatsappButton
            handleClick={handleWhatsappClick}
          />
        )}
      </ButtonsWrapper>

      <MobileSwipeableDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        hideBackdrop={false}
        zIndex={9999}
        containerSx={{ overflow: 'hidden' }}
      >
        <Stack
          sx={{
            background: '#fff',
            paddingBottom: 6,
          }}
        >
          <Stack padding={{ xs: '16px 16px 0 16px ', sm: '20px' }} direction="column" spacing={1}>
            <Stack direction="column" alignItems="center" spacing={2.5}>
              <Stack direction="row" spacing={2} width="100%" sx={{ overflow: 'hidden' }}>
                {project?.clients.map((client, index) =>
                  client?.logoUrl ? (
                    <img
                      key={client.id || `client-${index}`}
                      src={client.logoUrl}
                      alt={client.name || 'Client logo'}
                      style={{
                        objectFit: 'contain',
                        maxWidth: 'fit-content',
                        height: '60px',
                      }}
                    />
                  ) : null,
                )}
              </Stack>
              <Stack direction="column" alignItems="center" spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="h4" fontWeight={600}>
                    {t('labels.callUsNow')}
                  </Typography>
                </Stack>

                <Typography variant="subtitle1" fontWeight={400} textAlign="center">
                  {t('labels.tapNumberBelow')}
                </Typography>

                <ContainedButton
                  icon={phoneIcon}
                  color="success"
                  label={phoneNumber}
                  onClick={handleCall}
                  showLineAnimation={showLineAnimation}
                  labelDirection="ltr"
                  buttonSx={{ width: '100%' }}
                />
              </Stack>

              <Stack direction="row" spacing={1}>
                {memoIcon}
                <Typography variant="subtitle1" fontWeight={400}>
                  {t('labels.noSms')}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </MobileSwipeableDrawer>
    </>
  );
}
